import React from "react";

import { AppIcon as App, LockIcon as Lock } from "@packages/ds";
import { useIntl } from "react-intl";

import * as Accordion from "ds/Accordion";
import { Tooltip } from "ds/Tooltip";

import * as S from "./EnvironmentRoutes.styles";
import { useEnvironmentRoutes } from "./hooks/useEnvironmentRoutes";

type EnvironmentRoutes = {
  environmentId?: string;
  organizationId: string;
  projectId: string;
  routeMaxNumber: number;
  hideRedirects?: boolean;
};

const EnvironmentRoutes = ({
  environmentId,
  organizationId,
  projectId,
  hideRedirects = false,
  routeMaxNumber = 100
}: EnvironmentRoutes) => {
  const intl = useIntl();

  const { formattedRoutesWithApplications } = useEnvironmentRoutes({
    organizationId,
    projectId,
    environmentId: environmentId!,
    hideRedirects,
    routeMaxNumber
  });

  if (!environmentId) {
    return (
      <S.RoutesWrapper>
        {intl.formatMessage({ id: "dropdown.routes.no_env" })}
      </S.RoutesWrapper>
    );
  }

  return (
    <S.RoutesWrapper>
      {Object.keys(formattedRoutesWithApplications.apps ?? {}).length !== 0 &&
        Object.entries(formattedRoutesWithApplications.apps).map(
          ([appName, routes], index) => (
            <>
              <S.AppName size="small" weight="semiBold">
                {appName}
              </S.AppName>
              <Accordion.Root type="multiple" defaultValue={["item-0"]}>
                <Accordion.Item value={`item-${index}`}>
                  <S.Line>
                    <hr />
                  </S.Line>
                  <S.AccordionHeader>
                    <S.Title>
                      <S.AppNameWrapper>
                        <Tooltip
                          tooltip={intl.formatMessage({
                            id: "app"
                          })}
                          side="top"
                          analyticId="app"
                        >
                          <App />{" "}
                        </Tooltip>
                        <S.AppNameWorkers weight="semiBold">
                          {appName}
                          <S.AppNameNumber> ({routes.length})</S.AppNameNumber>
                        </S.AppNameWorkers>
                      </S.AppNameWrapper>
                    </S.Title>
                    <Accordion.Trigger>
                      <Accordion.Chevron />
                    </Accordion.Trigger>
                  </S.AccordionHeader>
                  <Accordion.Content>
                    {routes.map(({ id, url }, index) => (
                      <>
                        {id && (
                          <S.Instance weight="semiBold">ID: {id}</S.Instance>
                        )}
                        <S.URLContainer
                          key={`environment-url-cmd-div-${index}`}
                        >
                          {url}
                          <Lock />
                        </S.URLContainer>
                      </>
                    ))}
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            </>
          )
        )}
      {formattedRoutesWithApplications.redirects.length !== 0 && (
        <>
          <S.AppName size="small" weight="semiBold">
            {intl.formatMessage({ id: "redirects" })}
          </S.AppName>
          <Accordion.Root type="multiple" defaultValue={["item-0"]}>
            <Accordion.Item value="item-redirects">
              <S.Line>
                <hr />
              </S.Line>
              <S.AccordionHeader>
                <S.Title>
                  <S.AppNameWrapper>
                    <Tooltip
                      tooltip={intl.formatMessage({
                        id: "app"
                      })}
                      side="top"
                      analyticId="app"
                    >
                      <App />{" "}
                    </Tooltip>
                    <S.AppNameWorkers weight="semiBold">
                      {intl.formatMessage({ id: "redirects" })}
                      <S.AppNameNumber>
                        {" "}
                        ({formattedRoutesWithApplications.redirects.length})
                      </S.AppNameNumber>
                    </S.AppNameWorkers>
                  </S.AppNameWrapper>
                </S.Title>
                <Accordion.Trigger>
                  <Accordion.Chevron />
                </Accordion.Trigger>
              </S.AccordionHeader>
              <Accordion.Content>
                {formattedRoutesWithApplications.redirects.map(
                  (route, index) => (
                    <S.URLContainer
                      key={`environment-redirects-cmd-div-${index}`}
                    >
                      {route}
                    </S.URLContainer>
                  )
                )}
              </Accordion.Content>
            </Accordion.Item>
          </Accordion.Root>
        </>
      )}
    </S.RoutesWrapper>
  );
};

export default EnvironmentRoutes;
